import React, { useState, useEffect } from "react";
import { dataService } from "../../../../../Services/data/data.service";
import { Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import Button from "../../../../Shared/Buttons/Button";
import Input from "../../../../Shared/Input";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import { Plus, Search } from "lucide-react";
import { crudTypes, PriceBundleFilterTypes } from "../../../../../Config/constants";
import PriceBundleTable from "../PriceBundle/PriceBundleTable";
import PriceBundleDialog from "../PriceBundle/PriceBundleDialog";
import TablePagination from "@mui/material/TablePagination";

function PriceBundleTab(props) {
  const classes = useStyles();

  // General
  const [originalPriceBundles, setOriginalPriceBundles] = useState([]); // Store the original list of price definitions - unfiltered
  const [priceBundleList, setPriceBundleList] = useState([]); // Filtered and sorted
  const [filters, setFilters] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Price Definition Dialog
  const [selectedPriceBundle, setSelectedPriceBundle] = useState(null);
  const [priceBundleDialogOpen, setPriceBundleDialogOpen] = useState(false);
  const [crudType, setCrudType] = useState(null); // Create, Edit, View

  // Fetch Price Definitions
  useEffect(() => {
    dataService
      .getAllPriceBundles(props.Login.userid)
      .then((response) => {
        setOriginalPriceBundles(response);
        setPriceBundleList(response);
      })
      .catch((error) => {
        console.error("Error fetching price definitions:", error);
        // Handle error, e.g., show a message to the user
      });
  }, [props.Login.userid]);

  // Fetch Companies
  useEffect(() => {
    if (props.Login && props.Login.userid) {
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
        });
    }
  }, [props.Login.userid]);

  // Filter Logic
  const getFilterValue = (type) => {
    let filter = filters.find((x) => x.type === type);

    if (filter) {
      return filter.filterValue;
    } else {
      return "";
    }
  };

  const updateFilters = (type, event) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === type)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === type));
      tempFilters.splice(index, 1);
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    } else {
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    }
    setFilters(tempFilters);
    filterList(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const updateCompanyFilter = (selectedCompany) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === PriceBundleFilterTypes.COMPANY)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === PriceBundleFilterTypes.COMPANY));
      tempFilters.splice(index, 1);
      if (selectedCompany) {
        tempFilters.unshift({ type: PriceBundleFilterTypes.COMPANY, filterValue: selectedCompany.id });
      }
    } else {
      if (selectedCompany) {
        tempFilters.unshift({ type: PriceBundleFilterTypes.COMPANY, filterValue: selectedCompany.id });
      }
    }
    setFilters(tempFilters);
    filterList(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const filterList = (thisFilters, priceBundles) => {
    let varFilteredlist = priceBundles ? priceBundles : [...originalPriceBundles];

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case PriceBundleFilterTypes.SEARCH:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.NAME:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.REFERENCE:
          varFilteredlist = varFilteredlist.filter((x) => x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.COMPANY:
          varFilteredlist = varFilteredlist.filter((x) => x.companyId === filter.filterValue);
          break;
        default:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    
    // Sort alphabetically by reference
    varFilteredlist = varFilteredlist.sort((a, b) => a.reference.localeCompare(b.reference));
    
    if (priceBundles) {
      return varFilteredlist;
    } else {
      setPriceBundleList(varFilteredlist);
    }
  };

  // Create Price Definition Logic
  const openCreatePriceBundleDialog = () => {
    setCrudType(crudTypes.CREATE);
    setPriceBundleDialogOpen(true);
  };

  const closePriceBundleDialog = () => {
    setPriceBundleDialogOpen(false);
    setCrudType(null);
  };

  // Open the dialog to edit an existing price bundle
  const handleEditBundle = (priceBundleUniqueId) => {
    // Logic to open the dialog for editing an existing price definition
    const priceBundle = originalPriceBundles.find((x) => x.uniqueId === priceBundleUniqueId);
    if (!priceBundle) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceBundle(priceBundle);
    setCrudType(crudTypes.UPDATE);
    setPriceBundleDialogOpen(true);
  };

  // Open the dialog to view a price bundle
  const handleViewBundle = (priceBundleUniqueId) => {
    const priceBundle = originalPriceBundles.find((x) => x.uniqueId === priceBundleUniqueId);
    if (!priceBundle) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceBundle(priceBundle);
    setCrudType(crudTypes.READ);
    setPriceBundleDialogOpen(true);
  };

  // Add this handler to add a new definition
  const handleAddPriceBundle = (newBundle) => {
    const sortAlphabetically = (bundles) => {
      return [...bundles].sort((a, b) => a.reference.localeCompare(b.reference));
    };

    setOriginalPriceBundles((prev) => sortAlphabetically([...prev, newBundle]));
    
    // Apply current filters to the new list including the new bundle
    const newOriginalList = [...originalPriceBundles, newBundle];
    const filteredList = filterList(filters, newOriginalList);
    setPriceBundleList(sortAlphabetically(filteredList || newOriginalList));
  };

  // Add this handler to update an existing definition
  const handleUpdatePriceBundle = (updatedBundle) => {
    const sortAlphabetically = (bundles) => {
      return [...bundles].sort((a, b) => a.reference.localeCompare(b.reference));
    };

    setOriginalPriceBundles((prev) => sortAlphabetically(prev.map((def) => (def.uniqueId === updatedBundle.uniqueId ? updatedBundle : def))));
    
    // Apply current filters to the updated list
    const newOriginalList = originalPriceBundles.map((def) => (def.uniqueId === updatedBundle.uniqueId ? updatedBundle : def));
    const filteredList = filterList(filters, newOriginalList);
    setPriceBundleList(sortAlphabetically(filteredList || newOriginalList));
  };

  // Utility function to highlight search text
  const getSearchValue = () => {
    return getFilterValue(PriceBundleFilterTypes.SEARCH);
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(PriceBundleFilterTypes.COMPANY);
    return allCompanies.find((company) => company.id === companyId) || null;
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated rows
  const paginatedPriceBundles = priceBundleList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder="Search by name or reference"
              className={classes.search__input}
              type="text"
              value={getFilterValue(PriceBundleFilterTypes.SEARCH)}
              onChange={(e) => updateFilters(PriceBundleFilterTypes.SEARCH, e)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedCompany()?.id} onChange={updateCompanyFilter} size="small">
              <SelectTrigger>{getSelectedCompany()?.name || "All Companies"}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>All Companies</SelectItem>
                {allCompanies.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" startIcon={<Plus />} onClick={() => openCreatePriceBundleDialog()}>
            Create Price Bundle
          </Button>
        </div>
      </div>
      {/* Table */}
      <PriceBundleTable priceBundles={paginatedPriceBundles} handleEditBundle={handleEditBundle} handleViewBundle={handleViewBundle} searchValue={getSearchValue()} />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={priceBundleList.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Dialog */}
      <PriceBundleDialog
        dialogOpen={priceBundleDialogOpen}
        closeDialog={closePriceBundleDialog}
        crudType={crudType}
        priceBundle={selectedPriceBundle}
        onAdd={handleAddPriceBundle}
        onUpdate={handleUpdatePriceBundle}
        {...props}
      />
    </React.Fragment>
  );
}

export default PriceBundleTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid #C3996B",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },
});
